/**
 * Converts an image to the specified format
 * @param {HTMLImageElement} image - The original image element
 * @param {string} format - The target format (jpeg, png, or webp)
 * @param {number} quality - The quality level (1-100) for lossy formats
 * @param {HTMLCanvasElement} canvas - The canvas element to use for conversion
 * @returns {Promise<string>} - Promise resolving to the converted image data URL
 */
export const convertImage = (image, format, quality, canvas) => {
  return new Promise((resolve, reject) => {
    try {
      // Set canvas dimensions to match image
      canvas.width = image.width;
      canvas.height = image.height;
      
      // Draw image on canvas
      const ctx = canvas.getContext('2d');
      ctx.drawImage(image, 0, 0, canvas.width, canvas.height);
      
      // Determine MIME type and quality options
      let mimeType = 'image/png';
      let conversionQuality = undefined;
      
      switch (format.toLowerCase()) {
        case 'jpeg':
        case 'jpg':
          mimeType = 'image/jpeg';
          conversionQuality = quality / 100;
          break;
        case 'webp':
          mimeType = 'image/webp';
          conversionQuality = quality / 100;
          break;
        case 'png':
        default:
          mimeType = 'image/png';
          break;
      }
      
      // Convert to data URL with the specified format
      const dataUrl = canvas.toDataURL(mimeType, conversionQuality);
      resolve(dataUrl);
    } catch (error) {
      reject(error);
    }
  });
};

/**
 * Detects the format of an image from its data URL
 * @param {string} dataUrl - The data URL of the image
 * @returns {string} - The detected format (jpeg, png, webp, etc.)
 */
export const detectImageFormat = (dataUrl) => {
  const mimeTypeMatch = dataUrl.match(/^data:image\/([a-zA-Z0-9]+);base64,/);
  if (mimeTypeMatch && mimeTypeMatch[1]) {
    return mimeTypeMatch[1].toLowerCase();
  }
  return 'unknown';
}; 